import { supabase } from '@/integrations/supabase/client';

export interface TherapyAnalyticsData {
  therapy_id: string;
  therapy_name: string;
  booking_count: number;
  total_revenue: number;
  avg_rating: number | null;
  practitioner_count: number;
}

export interface TherapyAnalyticsSummary {
  total_bookings: number;
  total_revenue: number;
  average_rating: number;
  active_therapies: number;
}

export type TimeRange = 'month' | 'quarter' | 'year';

function getDateRangeStart(timeRange: TimeRange): Date {
  const now = new Date();
  switch (timeRange) {
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'quarter':
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      return new Date(now.getFullYear(), quarterStart, 1);
    case 'year':
      return new Date(now.getFullYear(), 0, 1);
    default:
      return new Date(now.getFullYear(), now.getMonth(), 1);
  }
}

export async function getTherapyAnalytics(timeRange: TimeRange): Promise<{
  data: TherapyAnalyticsData[];
  summary: TherapyAnalyticsSummary;
}> {
  const startDate = getDateRangeStart(timeRange);
  const startDateStr = startDate.toISOString();

  // Get all active therapies
  const { data: therapies, error: therapiesError } = await supabase
    .from('therapy_modalities')
    .select('id, name_en')
    .eq('is_active', true)
    .order('name_en');

  if (therapiesError) {
    console.error('Error fetching therapies:', therapiesError);
    return { data: [], summary: { total_bookings: 0, total_revenue: 0, average_rating: 0, active_therapies: 0 } };
  }

  // Get all services with their therapy tags
  const { data: services, error: servicesError } = await supabase
    .from('services')
    .select('id, practitioner_id, custom_therapy_tags');

  if (servicesError) {
    console.error('Error fetching services:', servicesError);
    return { data: [], summary: { total_bookings: 0, total_revenue: 0, average_rating: 0, active_therapies: 0 } };
  }

  // Get bookings in the time range
  const { data: bookings, error: bookingsError } = await supabase
    .from('bookings')
    .select('id, service_id, price_at_booking, status')
    .gte('created_at', startDateStr)
    .in('status', ['Completed', 'Confirmed', 'pending_confirmation']);

  if (bookingsError) {
    console.error('Error fetching bookings:', bookingsError);
    return { data: [], summary: { total_bookings: 0, total_revenue: 0, average_rating: 0, active_therapies: 0 } };
  }

  // Get reviews
  const { data: reviews, error: reviewsError } = await supabase
    .from('reviews')
    .select('booking_id, rating');

  if (reviewsError) {
    console.error('Error fetching reviews:', reviewsError);
  }

  // Create a map of booking_id to rating
  const bookingRatings = new Map<string, number>();
  reviews?.forEach(review => {
    if (review.booking_id && review.rating) {
      bookingRatings.set(review.booking_id, review.rating);
    }
  });

  // Create service to therapy mapping
  const serviceTherapyMap = new Map<string, string[]>();
  const therapyPractitionerMap = new Map<string, Set<string>>();

  services?.forEach(service => {
    const tags = service.custom_therapy_tags || [];
    serviceTherapyMap.set(service.id, tags);
    
    // Count practitioners per therapy
    tags.forEach((tag: string) => {
      if (!therapyPractitionerMap.has(tag)) {
        therapyPractitionerMap.set(tag, new Set());
      }
      therapyPractitionerMap.get(tag)!.add(service.practitioner_id);
    });
  });

  // Calculate stats per therapy
  const therapyStats = new Map<string, { 
    bookings: number; 
    revenue: number; 
    ratings: number[];
  }>();

  therapies?.forEach(therapy => {
    therapyStats.set(therapy.name_en, { bookings: 0, revenue: 0, ratings: [] });
  });

  bookings?.forEach(booking => {
    const therapyTags = serviceTherapyMap.get(booking.service_id) || [];
    const rating = bookingRatings.get(booking.id);
    
    therapyTags.forEach(tag => {
      const stats = therapyStats.get(tag);
      if (stats) {
        stats.bookings += 1;
        stats.revenue += booking.price_at_booking || 0;
        if (rating) {
          stats.ratings.push(rating);
        }
      }
    });
  });

  // Build result array
  const data: TherapyAnalyticsData[] = therapies?.map(therapy => {
    const stats = therapyStats.get(therapy.name_en) || { bookings: 0, revenue: 0, ratings: [] };
    const practitionerCount = therapyPractitionerMap.get(therapy.name_en)?.size || 0;
    const avgRating = stats.ratings.length > 0 
      ? stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length 
      : null;

    return {
      therapy_id: therapy.id,
      therapy_name: therapy.name_en,
      booking_count: stats.bookings,
      total_revenue: stats.revenue,
      avg_rating: avgRating,
      practitioner_count: practitionerCount,
    };
  }) || [];

  // Sort by booking count descending
  data.sort((a, b) => b.booking_count - a.booking_count);

  // Calculate summary
  const summary: TherapyAnalyticsSummary = {
    total_bookings: data.reduce((sum, t) => sum + t.booking_count, 0),
    total_revenue: data.reduce((sum, t) => sum + t.total_revenue, 0),
    average_rating: (() => {
      const validRatings = data.filter(t => t.avg_rating !== null);
      if (validRatings.length === 0) return 0;
      return validRatings.reduce((sum, t) => sum + (t.avg_rating || 0), 0) / validRatings.length;
    })(),
    active_therapies: data.filter(t => t.booking_count > 0).length,
  };

  return { data, summary };
}

export function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function formatRating(rating: number | null): string {
  if (rating === null) return '-';
  return rating.toFixed(1);
}
